import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { RouterConstant } from "../../routers/index";
import { useLayoutContext } from "../../layouts/general-layout";
import { CaptureService } from "../../services/capture-exercise/capture-exercise.service";
import Button from "../../components/button";
import ChatHeader from "../../components/chat-header";
import QRCode from "react-qr-code";
import { Loader, LoaderWhite } from "../../assets/images/index";
import "cropperjs/dist/cropper.css";
import "./capture-exercise.scss";
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  Crop,
  PixelCrop,
  convertToPixelCrop,
  } from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import { AudioService } from "../../services/text-to-audio/texttoaudio.service";
import { useExerciseStore, setPendingHomeworkUpload } from "../../utils/exerciseStore";
import { TrackingService } from "../../services/tracking/tracking.service";
import { useUserStore } from "../../utils/userStore";

export default function CaptureExercise() {
  const navigate = useNavigate();
  const location = useLocation();
  const { ROUTERS_PATH } = RouterConstant;
  const trackingServiceInstance = new TrackingService();
  const { t } = useLayoutContext();
  const [searchParams] = useSearchParams();
  const [sessionId] = useState(() => searchParams.get("sessionId") || Date.now().toString());
  const tokenFromUrl = searchParams.get("token") || "";
  const isSolution = searchParams.get("type") === "solution";
  const [uploadCount, setUploadCount] = useState(0);
  const [capturedImage, setCapturedImage] = useState<string | null>(location.state?.capturedImageUrl || null);
  const [isUploading, setIsUploading] = useState(false);
  const [showPermissionPopup, setShowPermissionPopup] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const { userInfo, user_attributes } = useUserStore();
  const isFemale = userInfo?.gender === "female" || user_attributes?.gender === 2 || user_attributes?.gender === "female";
  const genderSuffix = isFemale ? "_female" : "_male";
  const pageTitle = isSolution ? t("solutionCaptureTitle") : t("ExercisePhotography");
  const pageDescription = isSolution 
    ? t(`solutionCaptureCropInstruction${genderSuffix}`) 
    : t(`homeworkCaptureCropInstruction${genderSuffix}`);
  const [tempToken, setTempToken] = useState<string>("");
  const [QRCodeURL, setQRCodeURL] = useState<string | null>(null);
  const [shouldOpenCamera, setShouldOpenCamera] = useState(!location.state?.capturedImageUrl);
  const isMobile = /iPhone|iPad|Android|Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const captureService = new CaptureService();
  const [cropperInstance, setCropperInstance] = useState<any>(null);
  const [isImageLoading, setIsImageLoading] = useState(false); // New state for image loading
  const audioServiceInstance = new AudioService();
  const [imgSrc, setImgSrc] = useState('')
  const previewCanvasRef = useRef<HTMLCanvasElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  const hiddenAnchorRef = useRef<HTMLAnchorElement>(null)
  const blobUrlRef = useRef('')
  const [crop, setCrop] = useState<Crop>({
    unit: "%",
    x: 15,
    y: 20,
    width: 60,
    height: 40,
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>()

  useEffect(() => {
    // Reset state when component mounts (Safari navigation fix)
    if (location.state?.capturedImageUrl) {
      setCapturedImage(location.state.capturedImageUrl);
      setShouldOpenCamera(false);
    } else {
      setCapturedImage(null);
      setShouldOpenCamera(true);
    }
    setCameraError(null);
    setAuthError(null);
    setIsImageLoading(false);
    setShowPermissionPopup(false);
  }, [location.state]); // Added location.state dependency to handle resets on route changes

  useEffect(() => {    
    if (isMobile && shouldOpenCamera && !capturedImage && !showPermissionPopup) {
      captureImage();
      setShouldOpenCamera(false);
      setShowPermissionPopup(false);
    }
  }, [isMobile, shouldOpenCamera, capturedImage, showPermissionPopup]);

 
  useEffect(() => {
    // Cleanup function to revoke object URLs when component unmounts
    return () => {
      if (capturedImage && capturedImage.startsWith('blob:')) {
        URL.revokeObjectURL(capturedImage);
      }
    };
  }, []);

  useEffect(() => {
    if (sessionId && tokenFromUrl) {
      if (isSolution) {
        captureService.markUserImageSolutionActive(sessionId, tokenFromUrl)
          .catch(err => console.error("Error marking solution active:", err));
      } else {
        captureService.markExerciseActive(sessionId, tokenFromUrl)
          .catch(err => console.error("Error marking exercise active:", err));
      }
    }
  }, [isSolution, sessionId, tokenFromUrl]);

  useEffect(() => {
    if (!isMobile || !isSolution || !sessionId || !tokenFromUrl) return;

    let isMounted = true;
    let intervalId: NodeJS.Timeout | null = null;

    const checkSessionImages = async () => {
      try {
        const response = await captureService.getUserImageSolution(sessionId);
        if (isMounted && response) {
          const serverCount = response.images ? response.images.length : 0;
          if (serverCount === 0 && uploadCount > 0) {
            setUploadCount(0);
            setCapturedImage(null);
            setCropperInstance(null);
            setAuthError(null);
            setShouldOpenCamera(true);
          } else if (serverCount !== uploadCount) {
            setUploadCount(serverCount);
          }
        }
      } catch (err) {
        console.error("Error checking session images on mobile:", err);
      }
    };

    intervalId = setInterval(checkSessionImages, 3000);

    return () => {
      isMounted = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [isMobile, isSolution, sessionId, tokenFromUrl, uploadCount]);

  useEffect(() => {
    if (!isMobile && !location.state?.capturedImageUrl) {
      let isMounted = true;
      let timeoutId: NodeJS.Timeout | null = null;
      let pollingStopped = false;
  
      const fetchToken = async () => {
        try {
          const response = await captureService.GENERATETEMPTOKEN(sessionId);
          const { token } = response;
          setQRCodeURL(`${window.location.origin}/capture-exercise?sessionId=${sessionId}&token=${token}`);
          setTempToken(token);
          audioServiceInstance.handleTextToAudioByKey("qrcode_voice_exaplin", true);
  
          const poll = async () => {
            if (!isMounted || pollingStopped) return;
            try {
              const response = await captureService.GETVALIDATEEXECISE(sessionId);
              if (response?.status === "invalid_image" || response?.is_math_exercise === false) {
                pollingStopped = true;
                if (isMounted) {
                  setAuthError(t(`hwNotAMathExercise${genderSuffix}`));
                }
                return;
              }
              if (response?.status === "success" && (response.exercise_content || response.capture_image_b64)) {
                useExerciseStore.getState().setCaptureImageSessionId(sessionId);
                pollingStopped = true;
                if (response.exercise_id) {
                  navigate(ROUTERS_PATH.DISPLAYEXERCISE, {
                    state: { 
                      exerciseData: response,
                      from: location.state?.fromHomework ? "exercises-homework" : undefined
                    },
                  });
                } else {
                  navigate(ROUTERS_PATH.EXERCISES_HOMEWORK, {
                    state: { exerciseData: response, capturedImageUrl: response.capture_image_b64 },
                  });
                }
                return;
              }
            } catch (error: any) {
              console.error("Polling error:", error.response?.data || error.message);
            }
            if (!pollingStopped) {
              timeoutId = setTimeout(poll, 5000);
            }
          };
  
          poll();
  
          setTimeout(() => {
            pollingStopped = true;
            if (isMounted) {
              setAuthError(t("Polling timed out. Please try again."));
            }
          }, 5 * 60 * 1000);
  
        } catch (error) {
          console.error("Token generation failed:", error);
          setAuthError(t("QR code generation failed. Please log in."));
        }
      };
  
      fetchToken();
  
      return () => {
        isMounted = false;
        pollingStopped = true;
        if (timeoutId) clearTimeout(timeoutId);
      };
    }
  }, [isMobile, sessionId, navigate, ROUTERS_PATH.DISPLAYEXERCISE, t, location.state?.capturedImageUrl]);

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const { width, height } = e.currentTarget;    
    const persistentCrop: Crop = {
      unit: "%",
      x: 15,
      y: 20,
      width: 60,
      height: 40
    };
    setCrop(persistentCrop);
    setCompletedCrop(convertToPixelCrop(persistentCrop, width, height));
  }
const captureImage = () => {  
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/*";
  input.capture = "environment";
  
  // Show loading state immediately
  setIsImageLoading(true);
  
  input.onchange = async (event: any) => {
    const file = event.target.files[0];
    if (!file) {
      setIsImageLoading(false);
      return;
    }

    try {
      if (!file.type.startsWith("image/")) {
        setCameraError("Invalid file type. Please upload an image.");
        return;
      }

      // Display image immediately using object URL for faster rendering
      const objectUrl = URL.createObjectURL(file);
      setCapturedImage(objectUrl);
      setShouldOpenCamera(false);
      setShowPermissionPopup(false);
      setCameraError(null);
      setAuthError(null);
      
      // No need to process the image further for preview
      // The actual processing will happen during upload
      
    } catch (error) {
      console.error("Error processing image:", error);
      setCameraError("Failed to process the image. Please try again.");
    } finally {
      setIsImageLoading(false);
    }
  };

  input.click();
};

const uploadImage = async () => {
  setIsUploading(true);
  setAuthError(null);

  // Allow React to re-render and show the loader
  await new Promise(resolve => setTimeout(resolve, 0));  
    
  const startTime = performance.now();
  
  try {
    const image = imgRef.current;
    if (!image || !completedCrop) {
      throw new Error('Crop selection does not exist');
    }

    
    // Create canvas with optimized dimensions
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { 
      alpha: false,  // No transparency needed for JPEG
      willReadFrequently: false 
    });

    if (!ctx) {
      throw new Error('No 2d context');
    }

    const { naturalWidth, naturalHeight, width: elWidth, height: elHeight } = image;
    const crop = completedCrop;

    const naturalAspect = naturalWidth / naturalHeight;
    const elAspect = elWidth / elHeight;

    let renderedWidth, renderedHeight, xOffset, yOffset;

    if (naturalAspect > elAspect) {
      renderedWidth = elWidth;
      renderedHeight = elWidth / naturalAspect;
      xOffset = 0;
      yOffset = (elHeight - renderedHeight) / 2;
    } else {
      renderedHeight = elHeight;
      renderedWidth = elHeight * naturalAspect;
      yOffset = 0;
      xOffset = (elWidth - renderedWidth) / 2;
    }

    const finalScaleX = naturalWidth / renderedWidth;
    const finalScaleY = naturalHeight / renderedHeight;

    // Calculate the source rectangle on the original, full-resolution image.
    let sourceX = (crop.x - xOffset) * finalScaleX;
    let sourceY = (crop.y - yOffset) * finalScaleY;
    let sourceWidth = crop.width * finalScaleX;
    let sourceHeight = crop.height * finalScaleY;

    // --- START: NEWLY ADDED FIX ---
    // Clamp the coordinates to the image boundaries to prevent black bars.
    // This handles cases where the crop selection extends into the empty letterbox area.
    if (sourceX < 0) {
      sourceWidth += sourceX;
      sourceX = 0;
    }
    if (sourceY < 0) {
      sourceHeight += sourceY;
      sourceY = 0;
    }
    // Ensure the crop doesn't extend beyond the image's dimensions.
    if (sourceX + sourceWidth > naturalWidth) {
      sourceWidth = naturalWidth - sourceX;
    }
    if (sourceY + sourceHeight > naturalHeight) {
      sourceHeight = naturalHeight - sourceY;
    }
    // --- END: NEWLY ADDED FIX ---

    // Ensure the final dimensions are valid
    if (sourceWidth <= 0 || sourceHeight <= 0) {
      throw new Error("Crop resulted in an empty image.");
    }

    const MAX_DIMENSION = 1920;
    let finalWidth = sourceWidth;
    let finalHeight = sourceHeight;

    if (sourceWidth > MAX_DIMENSION || sourceHeight > MAX_DIMENSION) {
      const scale = Math.min(MAX_DIMENSION / sourceWidth, MAX_DIMENSION / sourceHeight);
      finalWidth = Math.round(sourceWidth * scale);
      finalHeight = Math.round(sourceHeight * scale);
    }


    canvas.width = finalWidth;
    canvas.height = finalHeight;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';    
    
    // Apply mild contrast and brightness boost to clean up background/Moiré noise and improve character contrast
    if ('filter' in ctx) {
      (ctx as any).filter = 'none';
    }
    
    ctx.drawImage(
      image,
      sourceX, sourceY, sourceWidth, sourceHeight,
      0, 0, finalWidth, finalHeight
    );
    
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => b ? resolve(b) : reject(new Error('Failed to create blob')),
        'image/jpeg',
        0.92
      );
    });

    const formData = new FormData();
    formData.append("image", blob);
    formData.append("session_id", sessionId);

    if (isSolution) {
      formData.append("image_index", String(uploadCount + 1));
      handleTracking("upload_solution_image");
      const response = await captureService.uploadUserImageSolution(formData, tokenFromUrl);
      if (response) {
        if (capturedImage?.startsWith('blob:')) {
          URL.revokeObjectURL(capturedImage);
        }
        setCapturedImage(null);
        setCropperInstance(null);
        setAuthError(null);
        
        const nextCount = uploadCount + 1;
        setUploadCount(nextCount);
        
        if (nextCount >= 5) {
          navigate(ROUTERS_PATH.UPLOADCOMPLETE);
        }
      } else {
        setAuthError(t("Image upload failed."));
      }
      return;
    }

    const beforeApiTime = performance.now();
    const durationMs = Math.round(beforeApiTime - startTime);
    handleTracking("upload_image");
    if (location.state?.fromHomework && !tokenFromUrl) {
      const croppedPreviewUrl = URL.createObjectURL(blob);
      if (capturedImage?.startsWith('blob:')) {
        URL.revokeObjectURL(capturedImage);
      }
      useExerciseStore.getState().setCaptureImageSessionId(sessionId);
      const uploadPromise = captureService.UPLOADIMAGE(formData, tokenFromUrl);
      setPendingHomeworkUpload({
        promise: uploadPromise,
        previewUrl: croppedPreviewUrl,
        sessionId,
      });
      navigate(ROUTERS_PATH.EXERCISES_HOMEWORK, {
        state: {
          pendingUpload: true,
          capturedImageUrl: croppedPreviewUrl,
        },
      });
      return;
    }
    if (tokenFromUrl) {
      if (capturedImage?.startsWith('blob:')) {
        URL.revokeObjectURL(capturedImage);
      }
      useExerciseStore.getState().setCaptureImageSessionId(sessionId);
      await new Promise<void>((resolve, reject) => {
        let settled = false;
        const settleResolve = () => {
          if (!settled) {
            settled = true;
            resolve();
          }
        };
        captureService
          .UPLOADIMAGE(formData, tokenFromUrl, (progressEvent: any) => {
            if (progressEvent?.total && progressEvent.loaded >= progressEvent.total) {
              setTimeout(settleResolve, 250);
            }
          })
          .then(() => settleResolve())
          .catch((err) => {
            if (!settled) {
              settled = true;
              reject(err);
            } else {
              console.error("Background mobile QR upload error:", err);
            }
          });
      });
      navigate(ROUTERS_PATH.UPLOADCOMPLETE, { state: { durationMs } });
      return;
    }
    const response = await captureService.UPLOADIMAGE(formData, tokenFromUrl);
    if (response) {
      if (response.is_math_exercise === false || response.status === "invalid_image") {
        setAuthError(t(`hwNotAMathExercise${genderSuffix}`));
        return;
      }

      if (capturedImage?.startsWith('blob:')) {
        URL.revokeObjectURL(capturedImage);
      }      
      useExerciseStore.getState().setCaptureImageSessionId(sessionId);
      if (tokenFromUrl) 
        navigate(ROUTERS_PATH.UPLOADCOMPLETE, { state: { durationMs } });
      else if (response.exercise_id) {
        navigate(ROUTERS_PATH.DISPLAYEXERCISE, {
          state: { 
            exerciseData: response,
            from: location.state?.fromHomework ? "exercises-homework" : undefined
          },
        });
      } else {
        navigate(ROUTERS_PATH.EXERCISES_HOMEWORK, {
          state: { exerciseData: response, capturedImageUrl: capturedImage },
        });
      }
    } else {      
      setAuthError(t("Image upload failed."));
    }
  } catch (error) {    
    console.error("Upload error:", error);
    // Ensure error is a proper Error object to get a message
    const errorMessage = error instanceof Error ? error.message : String(error);
    setAuthError(`${t("uploadFailed")}: ${errorMessage}`);
  } finally {
    setIsUploading(false);
  }
};

  const handleCaptureAnother = () => {  
    if (capturedImage && capturedImage.startsWith('blob:')) {
      URL.revokeObjectURL(capturedImage);
    }    
    setCapturedImage(null);
    setCropperInstance(null); // Clear the cropper instance
    setAuthError(null);
    if (isMobile) {
      setShouldOpenCamera(true);
    } else {
      captureImage();
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const PermissionPopup = () => (
    <div className="permission-popup">
      <p>{cameraError || t("Camera access is required.")}</p>
      <Button
        text={t("TryAgain")}
        color="blue"
        onClick={() => {
          setShowPermissionPopup(false);
          captureImage();
        }}
      />
    </div>
  );

  const handleTracking = (event_name: string) => {
    const properties: any = {
      source: "capture_exercise",
      selected_exercise:"",
      selected_question: "",
      selected_step_name: "",
    };

    trackingServiceInstance.trackEvent(event_name, properties);
  }  

  return (
    <div className={`full-template capture-exercise-page
      ${(isMobile || !!capturedImage) ? 'capture-exercise-page-mobile' : ''}
      ${isMobile && tokenFromUrl ? 'hide-back-btn' : ''}`}
    >
      <div><ChatHeader chatHeading={pageTitle} onBackClick={handleBackClick} /></div>
      <div className="capture-exercise-body">
        {capturedImage ? (
          <div className="capture-exercise-body-inner">
            <div className="capture-exercise-body-text">
              <p>{pageDescription}</p>
            </div>
            <div className="capture-exercise-image">
              <div className="cropper-container-wrapper">
                {isImageLoading ? (
                  <div className="image-loading-container">
                    <img src={Loader} alt="Loading..." />
                  </div>
                ) : (
                  <div className="cropper-container">
                    {isUploading && (
                      <div className="upload-overlay">
                        <img src={Loader} alt="Uploading..." />
                      </div>
                    )}                        
                    <ReactCrop
                      key={capturedImage}
                      crop={crop}
                      onChange={(c, percentCrop) => {
                        if (c.width && c.height && c.width >= 100 && c.height >= 20) {
                          setCrop(c);
                        }
                      }}
                      onComplete={(c) => setCompletedCrop(c)}
                      aspect={undefined}
                      minWidth={100}  
                      minHeight={20}                              
                      ruleOfThirds={true}
                      keepSelection={true}                                                    
                    >
                      <img
                        ref={imgRef}
                        alt="Crop me"
                        src={capturedImage}
                        className="cropper-image"
                        onLoad={onImageLoad}                                                         
                      />
                    </ReactCrop>
                  </div>
                )}
              </div>
            </div>            
          </div>
        ) : isMobile ? (
          <>
            <div className="awaiting-camera">
              {isSolution ? (
                <>
                  <p className="solution-upload-count">
                    {t("solutionUploadCount", { count: uploadCount })}
                  </p>
                  <p className="solution-capture-instruction">
                    {t("solutionCaptureInstruction")}
                  </p>
                </>
              ) : (
                <p><b>{t("AccessRequestContent")}</b></p>
              )}
              
              {isSolution && uploadCount > 0 ? (
                <div className="solution-capture-mobile-buttons">
                  {uploadCount < 5 && (
                    <Button 
                      text={t("captureAnotherPage")} 
                      color="blue" 
                      onClick={captureImage} 
                      disabled={isUploading} 
                    />
                  )}
                  <Button 
                    text={t("closeAndReturnToPC")} 
                    color="blue" 
                    className="border-button" 
                    onClick={() => navigate(ROUTERS_PATH.UPLOADCOMPLETE)} 
                    disabled={isUploading}
                  />
                </div>
              ) : (
                <>
                  {(!isSolution || uploadCount < 5) && (
                    <Button text={t("OpenCamera")} color="blue" onClick={captureImage} disabled={isUploading} />
                  )}
                </>
              )}
            </div>
            {(showPermissionPopup || cameraError) && <PermissionPopup />}
          </>
        ) : (
          <div className="capture-exercise-scanner">
            {QRCodeURL ? (
              <QRCode value={QRCodeURL} size={260} />
            ) : (
              <div className="loaderBlock">
                <img src={Loader} alt="Loader" />
              </div>
            )}
            <div className="capture-exercise-scanner-text">
              <h6>{pageTitle}</h6>
              <p>{t("captureExerciseScannerText")}</p>
            </div>
          </div>
        )}
      </div>
      {capturedImage ? (
        <>
          <div className="status-container">
            {authError && 
              <p className="error-text">{authError}</p>
            }
          </div>
          <div className="capture-exercise-buttons">
            {!location.state?.isFileUpload && (
              <Button text={t("captureAnother")} color="blue" className="border-button" onClick={handleCaptureAnother} disabled={isUploading} />
            )}
            <Button text={t("selectImage")} color="blue" onClick={uploadImage} disabled={isUploading || isImageLoading} loader={isUploading} is_white_loader={true}/>
          </div>
        </>
      ) : !isMobile ? (
        <div className="capture-exercise-buttons">
          <Button text={t("return")} color="blue" className="border-button" onClick={handleBackClick} />
        </div>
      ) : null}
    </div>
  );
}
